import { Phone, CheckCircle, Clock, MapPin, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const BreakdownRecoveryPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="24/7 Roadside Breakdown Recovery Hackney | Emergency Car Recovery East London"
        description="Fast 30-min breakdown recovery in Hackney & East London. 24/7 emergency roadside assistance, jump starts, towing service. Call 07492 639223 for immediate help."
        keywords="breakdown recovery hackney, roadside assistance east london, emergency breakdown service, car towing hackney, 24/7 recovery service, breakdown recovery near me, emergency car recovery london"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-900 via-red-800 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-red-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🚨 24/7 Emergency Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Roadside Breakdown Recovery in Hackney & East London
            </h1>
            <p className="text-xl text-slate-200 mb-8">
              Fast 30-minute emergency response across Hackney, Shoreditch, Stratford and all East London. Professional breakdown recovery service available 24/7 including nights, weekends and bank holidays.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-red-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">30 Min</p>
              <p className="text-sm text-slate-600">Avg Response Time</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">24/7</p>
              <p className="text-sm text-slate-600">Always Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">E1-E18</p>
              <p className="text-sm text-slate-600">All E Postcodes</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">20 Miles</p>
              <p className="text-sm text-slate-600">Coverage Radius</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Emergency Breakdown Recovery Service in Hackney
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              When your car breaks down in Hackney, Shoreditch, Stratford or anywhere across East London, every minute counts. Ovawell Autos provides fast, professional breakdown recovery with an average 30-minute response time across all E postcodes. Our local team knows every street in Hackney and can reach you faster than national recovery services.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              What We Cover - Breakdown Recovery Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Engine failures and breakdowns",
                "Battery problems and jump starts",
                "Flat tyres and tyre changes",
                "Fuel delivery and wrong fuel",
                "Overheating engines",
                "Electrical failures",
                "Clutch and gearbox problems",
                "Accident recovery",
                "Vehicle won't start",
                "Keys locked in car",
                "Steering or suspension issues",
                "Any roadside emergency"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Coverage Areas - Where We Operate
            </h3>
            <p className="text-slate-700 mb-4">
              We provide 24/7 breakdown recovery across Hackney and the following East London areas:
            </p>
            <div className="grid md:grid-cols-3 gap-3 mb-8">
              {[
                "Hackney (E5, E8, E9)",
                "Shoreditch (E1, E2)",
                "Bethnal Green (E2)",
                "Bow & Mile End (E3)",
                "Stratford (E15)",
                "Leyton (E10)",
                "Walthamstow (E17)",
                "West Ham (E13)",
                "Islington (N1, N7)",
                "Tower Hamlets (E14)",
                "Newham (E6, E12)",
                "20-mile radius"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2 bg-white p-3 rounded border">
                  <MapPin className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              How Our Breakdown Service Works
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-red-600">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2">Call Us Immediately</h4>
                      <p className="text-slate-600">
                        Phone {companyInfo.phone} or use our WhatsApp. Available 24/7 including nights and weekends. Tell us your location and the problem.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-red-600">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2">We Dispatch Immediately</h4>
                      <p className="text-slate-600">
                        Our closest recovery vehicle is dispatched to your location. Average 30-minute response time in Hackney, 45 minutes across East London.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-red-600">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2">Safe Recovery & Transport</h4>
                      <p className="text-slate-600">
                        We assess the problem, secure your vehicle, and transport it to your chosen destination in Hackney or East London. Fully insured throughout.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Ovawell for Breakdown Recovery in Hackney?
            </h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <Clock className="w-8 h-8 text-red-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">Faster Response Times</h4>
                  <p className="text-slate-600">
                    Based in Hackney, we reach you in 30-45 minutes average compared to 60-90 minutes for national services. Local knowledge means faster arrival.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <MapPin className="w-8 h-8 text-red-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">Local East London Experts</h4>
                  <p className="text-slate-600">
                    We know every street, shortcut and one-way system in Hackney, Shoreditch and all E postcodes. Navigate traffic better than distant services.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <Phone className="w-8 h-8 text-red-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">24/7 Availability</h4>
                  <p className="text-slate-600">
                    Available every hour of every day including bank holidays and Christmas. Whether it's 3am or 3pm, we're ready to help.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-red-600 mb-3" />
                  <h4 className="font-bold text-lg mb-2">Competitive Local Pricing</h4>
                  <p className="text-slate-600">
                    No expensive call center overhead. Fair prices for Hackney and East London customers with transparent quotes before we start.
                  </p>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Breakdown Recovery Prices - Hackney & East London
            </h3>
            <p className="text-slate-700 mb-4">
              Transparent pricing with no hidden charges:
            </p>
            <div className="bg-white rounded-lg border-2 p-6 mb-8">
              <ul className="space-y-3">
                <li className="flex justify-between items-center pb-3 border-b">
                  <span className="text-slate-700">Within Hackney (E5, E8, E9)</span>
                  <span className="font-bold text-lg">£60-£80</span>
                </li>
                <li className="flex justify-between items-center pb-3 border-b">
                  <span className="text-slate-700">Hackney to Stratford/Shoreditch</span>
                  <span className="font-bold text-lg">£70-£90</span>
                </li>
                <li className="flex justify-between items-center pb-3 border-b">
                  <span className="text-slate-700">Within 10 miles of Hackney</span>
                  <span className="font-bold text-lg">£80-£100</span>
                </li>
                <li className="flex justify-between items-center pb-3 border-b">
                  <span className="text-slate-700">Up to 20 miles</span>
                  <span className="font-bold text-lg">£100-£130</span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-slate-700">Night/Weekend surcharge</span>
                  <span className="font-bold text-lg">+30-50%</span>
                </li>
              </ul>
              <p className="text-sm text-slate-500 mt-4">
                * Prices include recovery, transport, and insurance. Non-running vehicles may incur additional charges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-red-900 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Broken Down in Hackney or East London?
          </h2>
          <p className="text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
            Call now for fast 30-minute breakdown recovery. Available 24/7 across all E postcodes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-red-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Emergency: {companyInfo.phone}
              </Button>
            </a>
            <a href={`https://wa.me/${companyInfo.whatsappNumber}?text=I need breakdown recovery in Hackney`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-red-900 text-lg px-8 py-6">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
